import React from "react";
import styled from "styled-components";
import { colors2, typoV2_CSS } from "oolib";
import {
  ButtonPrimaryCompact,
  ButtonSecondaryCompact,
} from "oolib/dist/v2/components/Buttons";
// Tile icons come straight from phosphor — FileCsv isn't in oolib's icon set.
import {
  CheckCircleIcon,
  WarningCircleIcon,
  FileCsvIcon,
  CheckIcon,
  XIcon,
} from "@phosphor-icons/react";

const ORANGE = "#ff6416";

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 450px;
`;

const Tile = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TileBadge = styled.div`
  position: absolute;
  top: 3px;
  right: 3px;
  display: flex;

  & > svg {
    background: ${ORANGE};
    border-radius: 50%;
  }
`;

const Col = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SpreadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* imported rows have no bar/subtext — center the single line on the tile */
  ${({ $fillTile }) => $fillTile && "min-height: 34px;"}
`;

const Title = styled.div`
  ${typoV2_CSS.UI_BODY_SEMIBOLD_SM_DF}
  color: ${({ $color }) => $color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const Count = styled.div`
  ${typoV2_CSS.UI_BODY_SEMIBOLD_SM_DF}
  font-family: "DM Mono", monospace; /* tabular counts — no mono style in oolib typo */
  color: ${({ $color }) => $color};
  flex: 0 0 auto;
`;

const Bar = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
`;

const BarSegment = styled.div`
  height: 6px;
  border-radius: 10px;
  background: ${({ $color }) => $color};
  flex: ${({ $frac }) => Math.max($frac, 0.01)} 1 0;
`;

const SubText = styled.div`
  ${({ $small }) =>
    $small ? typoV2_CSS.UI_CAPTION_SEMIBOLD_DF : typoV2_CSS.UI_BODY_SEMIBOLD_SM_DF}
  color: ${({ $color }) => $color};
  min-width: 0;
`;

const STATUS_CONFIG = {
  imported: {
    tileBg: colors2.green, tileColor: colors2.grey90, TileIcon: CheckCircleIcon,
    titleColor: colors2.grey90,
    ctaLabel: "View", ctaIcon: "ArrowLineUpRight",
  },
  importing: {
    tileBg: colors2.grey10, tileColor: colors2.grey90, TileIcon: FileCsvIcon,
    titleColor: colors2.grey90, barFill: colors2.grey90,
    subColor: colors2.grey50, subSmall: true,
  },
  failed: {
    tileBg: colors2.red, tileColor: colors2.white, TileIcon: WarningCircleIcon,
    titleColor: colors2.red, barFill: colors2.red,
    subColor: colors2.red,
    ctaLabel: "Retry", ctaIcon: "ArrowsClockwise", ctaDark: true,
  },
  importedWithFailures: {
    tileBg: ORANGE, tileColor: colors2.white, TileIcon: CheckCircleIcon, badge: true,
    titleColor: colors2.grey90,
    subColor: colors2.grey90,
    ctaLabel: "Failed Rows (CSV)", ctaIcon: "DownloadSimple", ctaDark: true,
  },
};

/**
 * One row of the multi-import progress card — a single component for all four
 * states: "imported" | "importing" | "failed" | "importedWithFailures".
 * Purely presentational; counts, copy (`subText` = ETA / error / failed-rows
 * line) and CTA handlers come from the caller. CTA renders only when its
 * handler (onView / onRetry / onDownloadFailedRows) is passed.
 */
export const ImportProgressItem = ({
  status = "importing",
  title,
  completedCount,
  totalCount,
  failedCount = 0,
  subText,
  onView,
  onRetry,
  onDownloadFailedRows,
  ctaDisabled = false,
}) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.importing;
  const onCta = { imported: onView, failed: onRetry, importedWithFailures: onDownloadFailedRows }[status];
  const frac = (n) => (totalCount ? (n || 0) / totalCount : 0);

  // 6px bar segments [color, fraction]; grey track fills the remainder.
  // "importedWithFailures" splits green/red with no track (they sum to total).
  const segments =
    status === "importing" || status === "failed"
      ? [[cfg.barFill, frac(completedCount)], [colors2.grey10, 1 - frac(completedCount)]]
      : status === "importedWithFailures"
      ? [[colors2.green, frac(completedCount)], [colors2.red, frac(failedCount)]]
      : null;

  // failed/withFailures put the CTA next to subText; imported keeps it inline with the title
  const ctaOnSubRow = status === "failed" || status === "importedWithFailures";
  const CtaComp = cfg.ctaDark ? ButtonPrimaryCompact : ButtonSecondaryCompact;
  const cta = cfg.ctaLabel && onCta && (
    <CtaComp
      icon={cfg.ctaIcon}
      onClick={onCta}
      disabled={ctaDisabled}
      style={{ flex: "0 0 auto" }}
    >
      {cfg.ctaLabel}
    </CtaComp>
  );

  return (
    <Row>
      <Tile $bg={cfg.tileBg}>
        <cfg.TileIcon size={24} color={cfg.tileColor} />
        {cfg.badge && (
          <TileBadge>
            <WarningCircleIcon size={14} color={colors2.white} weight="fill" />
          </TileBadge>
        )}
      </Tile>

      <Col>
        <SpreadRow $fillTile={status === "imported"}>
          <Title $color={cfg.titleColor} title={title}>
            {title}
          </Title>
          {status !== "imported" && completedCount != null && totalCount != null && (
            <Count $color={cfg.titleColor}>
              {completedCount}/{totalCount}
            </Count>
          )}
          {!ctaOnSubRow && cta}
        </SpreadRow>

        {segments && (
          <Bar>
            {segments
              .filter(([, f]) => f > 0)
              .map(([color, f], i) => (
                <BarSegment key={i} $color={color} $frac={f} />
              ))}
          </Bar>
        )}

        {(subText || (ctaOnSubRow && cta)) && (
          <SpreadRow>
            <SubText $color={cfg.subColor || colors2.grey50} $small={cfg.subSmall}>
              {subText}
            </SubText>
            {ctaOnSubRow && cta}
          </SpreadRow>
        )}
      </Col>
    </Row>
  );
};

// ---- Collapsed-header mini status pill (Figma: Dots-Design 22927-15491) ----

const Pill = styled.div`
  position: relative;
  overflow: hidden;
  width: 60px;
  height: 28px;
  border-radius: 24px;
  border: 1px solid ${colors2.grey10};
  background: ${({ $bg }) => $bg || "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 0 0 auto;
`;

// Left-rounded partial fill showing progress within the pill.
const PillFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $frac }) => Math.min($frac, 1) * 100}%;
  border-radius: 24px 0 0 24px;
  background: ${({ $overlay }) => ($overlay ? colors2.grey20 : colors2.grey30)};
`;

const PILL_CONFIG = {
  imported: { bg: colors2.green, icons: [CheckIcon], iconColor: colors2.black },
  importing: { fill: true, icons: [] },
  failed: { bg: colors2.red, overlay: true, icons: [XIcon], iconColor: colors2.white },
  importedWithFailures: {
    bg: ORANGE, overlay: true,
    icons: [CheckIcon, WarningCircleIcon], iconColor: colors2.white,
  },
};

/**
 * One import's status compressed into a 60x28 pill — shown in the card header
 * when the popup is collapsed. `fraction` = completed/total (used by the
 * importing fill and the failed/withFailures progress overlay).
 */
export const ImportStatusPill = ({ status = "importing", fraction = 0 }) => {
  const cfg = PILL_CONFIG[status] || PILL_CONFIG.importing;
  return (
    <Pill $bg={cfg.bg}>
      {(cfg.fill || cfg.overlay) && (
        <PillFill $frac={fraction} $overlay={cfg.overlay} />
      )}
      {cfg.icons.map((Icon, i) => (
        <Icon key={i} size={16} color={cfg.iconColor} weight="bold" style={{ position: "relative" }} />
      ))}
    </Pill>
  );
};
